import { useAuth } from '@hostesa/auth-client';
import { useT } from '@hostesa/i18n';
import { type ChartSeries, EmptyState, LineChartCard, MetricCard, PageHeader } from '@hostesa/ui';
import { useHotelContext } from '../../lib/hotel-context';
import { ANALYTICS_KPIS, ANALYTICS_TREND } from './fixtures';
import { namespace } from './i18n';

const PAGE_WRAP = 'flex flex-col gap-6';

/**
 * ANALYTICS dashboard — the Intelligence section. Printed KPI tiles (calls,
 * bookings, average handling time, languages) over a calls-vs-bookings trend.
 *
 * The backend exposes no analytics rollup yet, so figures come from a fixture
 * seam (`./fixtures`, // SEAM →); when the aggregate endpoint lands the values
 * swap in without touching the layout. Guard: RBAC `call:read`.
 */
export function AnalyticsPage() {
  const { t } = useT(namespace);
  const { timezone } = useHotelContext();
  const { can } = useAuth();

  // Keep the timezone wired so hotel-tz-bucketed data slots in unchanged later.
  void timezone;

  if (!can('call:read')) {
    return (
      <div data-testid="analytics-page" className={PAGE_WRAP}>
        <PageHeader
          eyebrow={t('page.eyebrow')}
          title={t('page.title')}
          description={t('page.description')}
        />
        <EmptyState
          data-testid="analytics-no-access-state"
          title={t('noAccess.title')}
          description={t('noAccess.description')}
        />
      </div>
    );
  }

  const series: ChartSeries[] = [
    {
      id: 'calls',
      label: t('trend.calls'),
      color: 'var(--color-charcoal)',
      data: ANALYTICS_TREND.calls,
      filled: true,
    },
    {
      id: 'bookings',
      label: t('trend.bookings'),
      color: 'var(--color-warm-stone)',
      data: ANALYTICS_TREND.bookings,
    },
  ];

  return (
    <div data-testid="analytics-page" className={PAGE_WRAP}>
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.description')}
      />

      <div
        data-testid="analytics-metrics"
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
      >
        <MetricCard
          data-testid="metric-calls-handled"
          label={t('metrics.callsHandled.label')}
          value={ANALYTICS_KPIS.callsHandled.value}
          delta={ANALYTICS_KPIS.callsHandled.delta}
          trend={ANALYTICS_KPIS.callsHandled.trend}
        />
        <MetricCard
          data-testid="metric-bookings"
          label={t('metrics.bookings.label')}
          value={ANALYTICS_KPIS.bookings.value}
          delta={ANALYTICS_KPIS.bookings.delta}
          trend={ANALYTICS_KPIS.bookings.trend}
        />
        <MetricCard
          data-testid="metric-aht"
          label={t('metrics.aht.label')}
          value={ANALYTICS_KPIS.aht.value}
          delta={ANALYTICS_KPIS.aht.delta}
          trend={ANALYTICS_KPIS.aht.trend}
        />
        <MetricCard
          data-testid="metric-languages"
          label={t('metrics.languages.label')}
          value={ANALYTICS_KPIS.languages.value}
          delta={ANALYTICS_KPIS.languages.delta}
          trend={ANALYTICS_KPIS.languages.trend}
        />
      </div>

      <LineChartCard
        data-testid="analytics-chart"
        title={t('trend.title')}
        series={series}
        xLabels={ANALYTICS_TREND.xLabels}
      />
    </div>
  );
}
